/**
 * Centralized Storage Manager
 * Prevents race conditions and provides debounced writes to localStorage
 */

interface StorageItem {
  value: any;
  timestamp: number;
  type: 'string' | 'object' | 'number' | 'boolean';
}

class StorageManager {
  private static instance: StorageManager;
  private debounceTimers = new Map<string, NodeJS.Timeout>();
  private readonly DEFAULT_DEBOUNCE = 300; // 300ms default debounce

  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  /**
   * Set item with debouncing to prevent race conditions
   */
  setItem(key: string, value: any, debounceMs = this.DEFAULT_DEBOUNCE): void {
    // Clear existing timer for this key
    const existingTimer = this.debounceTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Determine the type for proper retrieval
    const rawType = typeof value;
    const type: 'string' | 'object' | 'number' | 'boolean' = 
      Array.isArray(value) || (rawType === 'object' && value !== null) ? 'object' :
      rawType === 'string' ? 'string' :
      rawType === 'number' ? 'number' :
      rawType === 'boolean' ? 'boolean' : 'string'; // fallback to string
    
    // Set new debounced timer
    const timer = setTimeout(() => {
      try {
        const storageItem: StorageItem = {
          value,
          timestamp: Date.now(),
          type
        };

        const serializedValue = JSON.stringify(storageItem);
        localStorage.setItem(key, serializedValue);
        
        console.log(`✅ Storage: Set ${key} (${type})`);
        this.debounceTimers.delete(key);
        
      } catch (error) {
        console.error(`❌ Storage error for key "${key}":`, error);
        // Fallback to simple string storage
        try {
          localStorage.setItem(key, String(value));
        } catch (fallbackError) {
          console.error(`❌ Fallback storage failed for key "${key}":`, fallbackError);
        }
      }
    }, debounceMs);

    this.debounceTimers.set(key, timer);
  }

  /**
   * Get item with proper type restoration
   */
  getItem<T>(key: string): T | null {
    try {
      const rawValue = localStorage.getItem(key);
      if (rawValue === null) {
        return null;
      }

      // Try to parse as StorageItem first
      try {
        const storageItem: StorageItem = JSON.parse(rawValue);
        
        // Validate StorageItem structure
        if (storageItem && 
            typeof storageItem === 'object' && 
            'value' in storageItem && 
            'timestamp' in storageItem && 
            'type' in storageItem) {
          
          return storageItem.value as T;
        }
      } catch {
        // Not a StorageItem, try direct JSON parsing
      }

      // Fallback to direct JSON parsing
      try {
        return JSON.parse(rawValue) as T;
      } catch {
        // Return as string if all else fails
        return rawValue as T;
      }

    } catch (error) {
      console.error(`❌ Error retrieving "${key}":`, error);
      return null;
    }
  }

  /**
   * Immediate write without debouncing (use sparingly)
   */
  setItemImmediate(key: string, value: any): void {
    // Clear any pending debounced write
    const existingTimer = this.debounceTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.debounceTimers.delete(key);
    }

    try {
      const rawType = typeof value;
      const type: 'string' | 'object' | 'number' | 'boolean' = 
        Array.isArray(value) || (rawType === 'object' && value !== null) ? 'object' :
        rawType === 'string' ? 'string' :
        rawType === 'number' ? 'number' :
        rawType === 'boolean' ? 'boolean' : 'string'; // fallback to string
        
      const storageItem: StorageItem = {
        value,
        timestamp: Date.now(),
        type
      };

      localStorage.setItem(key, JSON.stringify(storageItem));
      console.log(`⚡ Storage: Immediate set ${key} (${type})`);
      
    } catch (error) {
      console.error(`❌ Immediate storage error for "${key}":`, error);
      // Fallback
      try {
        localStorage.setItem(key, String(value));
      } catch (fallbackError) {
        console.error(`❌ Immediate fallback failed for "${key}":`, fallbackError);
      }
    }
  }

  /**
   * Remove item and clear any pending writes
   */
  removeItem(key: string): void {
    // Clear pending write
    const existingTimer = this.debounceTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.debounceTimers.delete(key);
    }

    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Storage: Removed ${key}`);
    } catch (error) {
      console.error(`❌ Error removing "${key}":`, error);
    }
  }

  /**
   * Check if key exists
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Clear all pending writes and flush immediately
   */
  flush(): void {
    console.log(`🚀 Storage: Flushing ${this.debounceTimers.size} pending writes`);
    
    this.debounceTimers.forEach((timer, key) => {
      clearTimeout(timer);
      // Trigger immediate write by calling the timer function
    });
    
    this.debounceTimers.clear();
  }

  /**
   * Get storage info for debugging
   */
  getStorageInfo(): {
    pendingWrites: number;
    storageSize: number;
    keys: string[];
  } {
    const keys = Object.keys(localStorage);
    const storageSize = new Blob(Object.values(localStorage)).size;
    
    return {
      pendingWrites: this.debounceTimers.size,
      storageSize,
      keys
    };
  }

  /**
   * Clean up on app close
   */
  cleanup(): void {
    this.flush();
  }
}

// Create singleton instance
export const storage = StorageManager.getInstance();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    storage.cleanup();
  });
}